<template>
  <ContractReviewDisplayComponent
    :accountData="accountData"
    :contractsToShow="contracts"
    :signerFields="signerFields"
    :companyFields="companyFields"
    :generatorFields="generatorFields"
    :contractState="contractState"
    :hasGenerator="hasGenerator"
    @createContainerForStartSigningReady="
      $emit('createContainerForStartSigningReady', $event)
    "
    @disableChangingInformation="$emit('disableChangingInformation')"
  />
</template>

<script setup lang="ts">
import { useGeneratorFinder } from "@/composables/useGeneratorFinder";
import { useCompanyFinder } from "@/composables/useCompanyFinder";
import { Account } from "@/types";
import { useOptionalContractInclusion } from "@/signup/composables/useOptionalContractInclusion";
import { useRoute } from "vue-router";
import { reactive, computed } from "vue";
import ContractReviewDisplayComponent from "@/signup/ContractReviewDisplayComponent.vue";
import { useI18n } from "vue-i18n";
import {
  LT_LITGRID_TERMS,
  LT_AUTHORIZATION_APPLICATION,
  LT_DEVICE_REGISTRATION,
  LT_REQUEST_FOR_GO,
  LT_INSPECTION_REPORT,
  LT_PROPERTY_LIMIT_SCHEME,
  LT_SOLDERA_MEMBERSHIP,
  LT_POWER_OF_ATTORNEY,
} from "@/signup/contract_keys";

const props = withDefaults(
  defineProps<{
    accountData: Account;
    requiresPoA?: boolean;
  }>(),
  { requiresPoA: true }
);

defineEmits<{
  createContainerForStartSigningReady: [
    createContainerForStartSigning: () => Promise<void>,
  ];
  disableChangingInformation: [];
}>();

const { t } = useI18n();
const { companyIndex } = useCompanyFinder(props.accountData);
const selectedCompany = computed(
  () => props.accountData.companies[companyIndex.value]
);
const { nullableSelectedGenerator } = useGeneratorFinder(props.accountData);
const getGeneratorFileStatus = (fileKey: string) => {
  return computed(() => {
    if (
      nullableSelectedGenerator.value &&
      nullableSelectedGenerator.value.generator &&
      nullableSelectedGenerator.value.generator.files
    ) {
      return fileKey in nullableSelectedGenerator.value.generator.files
        ? t("common.uploaded")
        : t("common.not_uploaded");
    } else {
      return t("common.not_uploaded");
    }
  });
};

const route = useRoute();
const addAuthorizationAgreement = computed(() => {
  return route.query.addAuthorizationAgreement === "true";
});

const contractState = computed(() => {
  if (addAuthorizationAgreement.value) {
    return 2;
  } else if (
    // Second device registration flow
    hasSignedOptionalContract(LT_LITGRID_TERMS) ||
    hasSignedOptionalContract(LT_AUTHORIZATION_APPLICATION)
  ) {
    return 3;
  }
  return 1;
});

const hasGenerator = computed(() => {
  return (
    !addAuthorizationAgreement.value &&
    nullableSelectedGenerator.value.generator !== null
  );
});

const signerFields = reactive([
  { label: t("account.name"), value: props.accountData.name },
  { label: t("account.email"), value: props.accountData.email },
  {
    label: t("account.phone_number"),
    value: props.accountData.phone_number,
  },
]);

const companyFields = computed(() => {
  const baseFields = [
    { label: t("company.name"), value: selectedCompany.value.name },
    {
      label: t("company.registry_code"),
      value: selectedCompany.value.registry_code,
    },
  ];

  if (addAuthorizationAgreement.value) {
    if (props.requiresPoA) {
      return [
        ...baseFields,
        {
          label: t("signup.multiple_upload.lt_power_of_attorney"),
          value: getGeneratorFileStatus(LT_POWER_OF_ATTORNEY),
        },
      ];
    } else {
      return baseFields;
    }
  } else {
    return [
      ...baseFields,
      { label: t("company.vat"), value: selectedCompany.value.vat },
      {
        label: t("company.bank_account"),
        value: selectedCompany.value.bank_account,
      },
      {
        label: t("company.bank_name"),
        value: selectedCompany.value.bank_name,
      },
      {
        label: t("company.bank_swift"),
        value: selectedCompany.value.bank_swift,
      },
    ];
  }
});

const generatorFields = reactive([
  {
    label: t("generator.license_number"),
    value: nullableSelectedGenerator.value?.generator?.license_number,
  },
  {
    label: t("signup.multiple_upload.lt_inspection_report"),
    value: getGeneratorFileStatus(LT_INSPECTION_REPORT),
  },
  {
    label: t("signup.multiple_upload.lt_property_limit_scheme"),
    value: getGeneratorFileStatus(LT_PROPERTY_LIMIT_SCHEME),
  },
  {
    label: t("signup.multiple_upload.lt_power_of_attorney"),
    value: getGeneratorFileStatus(LT_POWER_OF_ATTORNEY),
  },
]);

const { hasSignedOptionalContract, includeDocument } =
  useOptionalContractInclusion(selectedCompany, contractState);

const litgridTOSContainer = [
  {
    id: 1,
    key: LT_LITGRID_TERMS,
    contract_title: t(
      "signup.contract_review_component.lt.lt_litgrid_terms_service_title"
    ),
    contract_description: t(
      "signup.contract_review_component.lt.lt_litgrid_terms_service_description"
    ),
    include: includeDocument([1], LT_LITGRID_TERMS).value,
    can_include: true,
  },
];
const mainContainer = [
  {
    id: 1,
    key: LT_DEVICE_REGISTRATION,
    contract_title: t(
      "signup.contract_review_component.lt.lt_device_registration_title"
    ),
    contract_description: t(
      "signup.contract_review_component.lt.lt_device_registration_description"
    ),
    include: includeDocument([3]).value,
    can_include: true,
  },
  {
    id: 2,
    key: LT_REQUEST_FOR_GO,
    contract_title: t(
      "signup.contract_review_component.lt.lt_request_for_go_contract_title"
    ),
    contract_description: t(
      "signup.contract_review_component.lt.lt_request_for_go_contract_description"
    ),
    include: includeDocument([1], LT_REQUEST_FOR_GO).value,
    can_include: true,
  },
  {
    id: 3,
    key: LT_INSPECTION_REPORT,
    contract_title: t("signup.multiple_upload.lt_inspection_report"),
    contract_description: t(
      "signup.contract_review_component.self_uploaded_file_text"
    ),
    include: includeDocument([1, 3]).value,
    can_include: true,
  },
  {
    id: 4,
    key: LT_PROPERTY_LIMIT_SCHEME,
    contract_title: t("signup.multiple_upload.lt_property_limit_scheme"),
    contract_description: t(
      "signup.contract_review_component.self_uploaded_file_text"
    ),
    include: includeDocument([1, 3]).value,
    can_include: true,
  },
  {
    id: 5,
    key: LT_AUTHORIZATION_APPLICATION,
    contract_title: t(
      "signup.contract_review_component.lt.lt_authotization_application_title"
    ),
    contract_description: t(
      "signup.contract_review_component.lt.lt_authorization_application_text"
    ),
    include: includeDocument([1, 2], LT_AUTHORIZATION_APPLICATION).value,
    can_include: true,
  },
  {
    id: 6,
    key: LT_SOLDERA_MEMBERSHIP,
    contract_title: t(
      "signup.contract_review_component.lt.lt_soldera_membership_agreement_title"
    ),
    contract_description: t(
      "signup.contract_review_component.lt.lt_soldera_membership_agreement_text"
    ),
    include: includeDocument([1, 2, 3], LT_SOLDERA_MEMBERSHIP).value,
    can_include: true,
  },
  {
    id: 7,
    key: LT_POWER_OF_ATTORNEY,
    contract_title: t("signup.multiple_upload.lt_power_of_attorney"),
    contract_description: t(
      "signup.contract_review_component.self_uploaded_file_text"
    ),
    include: includeDocument([1, 2, 3]).value && props.requiresPoA,
    can_include: true,
  },
  {
    id: 8,
    key: LT_LITGRID_TERMS,
    contract_title: t(
      "signup.contract_review_component.lt.lt_litgrid_terms_service_title"
    ),
    contract_description: t(
      "signup.contract_review_component.lt.lt_litgrid_terms_service_description"
    ),
    include: includeDocument([10], LT_LITGRID_TERMS).value, // Include document 10, because it should only be able to be added by admin
    can_include: true,
  },
];

// https://www.notion.so/solidworld/Lithuania-b85e093eb9544d8c86e122853d223ef5?pm=c
const contracts = computed(() => {
  if (litgridTOSContainer[0].include) {
    return [mainContainer, litgridTOSContainer];
  } else {
    return [mainContainer];
  }
});
</script>

<style scoped lang="scss"></style>
